import z from 'zod'

export enum amOwnershipPOCProviderEnum {
  oc = 'oc',
  cherre = 'cherre',
}
export const amOwnershipPOCProviderShema = z.nativeEnum(amOwnershipPOCProviderEnum)
export type AMOwnershipPOCProvider = z.infer<typeof amOwnershipPOCProviderShema>

export const amOwnershipPOCOptionsShema = z.object({
  hops: z.number().min(0).max(4),
  minSimilarScore: z.number().min(0.5).max(1),
  mergeScore: z.number().min(0.5).max(1),
  merge: z.boolean(),
})
export type AMOwnershipPOCOptions = z.infer<typeof amOwnershipPOCOptionsShema>

export const amOwnershipPOCGetFromParcelRequestShema = z.object({
  taxAssessorId: z.number(),
  options: amOwnershipPOCOptionsShema,
})
export type AMOwnershipPOCGetFromParcelRequest = z.infer<typeof amOwnershipPOCGetFromParcelRequestShema>

export const amOwnershipPOCGetEnrichInfoRequestShema = z.object({
  name: z.string(),
  address: z.string(),
})
export type AMOwnershipPOCGetEnrichInfoRequest = z.infer<typeof amOwnershipPOCGetEnrichInfoRequestShema>

export enum amOwnershipPOCEntityTypeEnum {
  parcel = 'parcel',
  person = 'person',
  organization = 'organization',
  unknown = 'unknown',
}
export const amOwnershipPOCEntityTypeShema = z.nativeEnum(amOwnershipPOCEntityTypeEnum)
export type AMOwnershipPOCEntityType = z.infer<typeof amOwnershipPOCEntityTypeShema>

export enum amOwnershipPOCEntityInternalTypeEnum {
  cherre_parcel = 'cherre_parcel',
  cherre_owner = 'cherre_owner',
  oc_company = 'oc_company',
  oc_officer = 'oc_officer',
}
export const amOwnershipPOCEntityInternalTypeShema = z.nativeEnum(amOwnershipPOCEntityInternalTypeEnum)
export type AMOwnershipPOCEntityInternalType = z.infer<typeof amOwnershipPOCEntityInternalTypeShema>

const amOwnershipPOCEntityShemaBase = z.object({
  uid: z.string(),
  depth: z.number(),
  name: z.string(),
  address: z.string(),
  type: amOwnershipPOCEntityTypeShema,
  provider: amOwnershipPOCProviderShema,
  internalType: amOwnershipPOCEntityInternalTypeShema,

  // Status
  statusDiscovered: z.boolean().optional(),
  statusExpandedParcel: z.boolean().optional(),
  statusExpandedOwner: z.boolean().optional(),
  statusExpandedCompany: z.boolean().optional(),
  statusExpandedOfficers: z.boolean().optional(),

  // Contact
  contact: z
    .object({
      emails: z.boolean(),
      workEmail: z.boolean(),
      phoneNumbers: z.boolean(),
      mobilePhone: z.boolean(),
    })
    .optional(),
})

export const amOwnershipPOCEntityShema = amOwnershipPOCEntityShemaBase.extend({
  mergedEntities: z.array(amOwnershipPOCEntityShemaBase),
})

export type AMOwnershipPOCEntity = z.infer<typeof amOwnershipPOCEntityShema>

export const amOwnershipPOCEntityCherreParcelDBShema = z.object({
  id: z.number(),
  address: z.string(),
  apn: z.string(),
  parentUid: z.string().optional(),
})
export type AMOwnershipPOCEntityCherreParcelDB = z.infer<typeof amOwnershipPOCEntityCherreParcelDBShema>

export const amOwnershipPOCEntityCherreParcelShema = amOwnershipPOCEntityShema.extend({
  taxAssessorId: z.number(),
  apn: z.string(),
})
export type AMOwnershipPOCEntityCherreParcel = z.infer<typeof amOwnershipPOCEntityCherreParcelShema>

export enum amOwnershipPOCRelationTypeEnum {
  owner = 'owner',
  officer = 'officer',
  might_be = 'might_be',
}
export const amOwnershipPOCRelationTypeShema = z.nativeEnum(amOwnershipPOCRelationTypeEnum)
export type AMOwnershipPOCRelatonType = z.infer<typeof amOwnershipPOCRelationTypeShema>

export const amOwnershipPOCRelationShema = z.object({
  score: z.number(),
  type: amOwnershipPOCRelationTypeShema,
  scoreName: z.number().optional(),
  scoreAddress: z.number().optional(),
})
export type AMOwnershipPOCRelation = z.infer<typeof amOwnershipPOCRelationShema>

export type AMOwnershipPOCRelations = Record<string, AMOwnershipPOCRelation>

export const amOwnershipPOCEntityCherreOwnerDBShema = z.object({
  id: z.number(),
  name: z.string(),
  address: z.string(),
  type: amOwnershipPOCEntityTypeShema,
  provider: amOwnershipPOCProviderShema,
  internalType: amOwnershipPOCEntityInternalTypeShema,
  zipcode: z.number(),
  vectorName: z.string().optional(),
  vectorAddress: z.string().optional(),
  taxAssessorId: z.number(),
})
export type AMOwnershipPOCEntityCherreOwnerDB = z.infer<typeof amOwnershipPOCEntityCherreOwnerDBShema>

export const amOwnershipPOCEntityCherreOwnerVecotorsDBShema = amOwnershipPOCEntityCherreOwnerDBShema.extend({
  nameScore: z.number(),
  addressScore: z.number(),
})
export type AMOwnershipPOCEntityCherreOwnerVectorsDB = z.infer<typeof amOwnershipPOCEntityCherreOwnerVecotorsDBShema>

export const amOwnershipPOCEntityCherreOwnerShema = amOwnershipPOCEntityShema.extend({
  id: z.number(),
  taxAssessorId: z.number(),
  zipcode: z.string(),
  vectors: z.object({
    name: z.array(z.number()),
    address: z.array(z.number()),
  }),
  scores: z.object({
    name: z.number(),
    address: z.number(),
  }),
})
export type AMOwnershipPOCEntityCherreOwner = z.infer<typeof amOwnershipPOCEntityCherreOwnerShema>

export const amOwnershipPOCEntityOCCompanyDBShema = z.object({
  id: z.string(),
  prefix: z.string(),
  name: z.string(),
  type: amOwnershipPOCEntityTypeShema,
  provider: amOwnershipPOCProviderShema,
  internalType: amOwnershipPOCEntityInternalTypeShema,
  address: z.string(),
  companyType: z.string(),
  currentStatus: z.string(),
  incorporationDate: z.string(),
  dissolutionDate: z.string(),
  zipcode: z.number(),
  vectorName: z.string().optional(),
  vectorAddress: z.string().optional(),
  nameScore: z.number(),
  addressScore: z.number(),
  childrenUid: z.string().optional(),
})
export type AMOwnershipPOCEntityOCCompanyDB = z.infer<typeof amOwnershipPOCEntityOCCompanyDBShema>

export const amOwnershipPOCEntityOCCompanyShema = amOwnershipPOCEntityShema.extend({
  id: z.string(),
  prefix: z.string(),
  companyType: z.string(),
  companyNumber: z.string(),
  currentStatus: z.string(),
  incorporationDate: z.string(),
  dissolutionDate: z.string(),
  zipcode: z.number(),
  vectors: z.object({
    name: z.array(z.number()),
    address: z.array(z.number()),
  }),
  scores: z.object({
    name: z.number(),
    address: z.number(),
  }),
})
export type AMOwnershipPOCEntityOCCompany = z.infer<typeof amOwnershipPOCEntityOCCompanyShema>

export const amOwnershipPOCEntityOCOfficerDBShema = z.object({
  id: z.string(),
  prefix: z.string(),
  name: z.string(),
  type: amOwnershipPOCEntityTypeShema,
  provider: amOwnershipPOCProviderShema,
  internalType: amOwnershipPOCEntityInternalTypeShema,
  address: z.string(),
  zipcode: z.number(),
  companyNumber: z.string(),
  position: z.string(),
  vectorName: z.string().optional(),
  vectorAddress: z.string().optional(),
  nameScore: z.number(),
  addressScore: z.number(),
  parentUid: z.string().optional(),
})
export type AMOwnershipPOCEntityOCOfficerDB = z.infer<typeof amOwnershipPOCEntityOCOfficerDBShema>

export const amOwnershipPOCEntityOCOfficerShema = amOwnershipPOCEntityShema.extend({
  id: z.string(),
  prefix: z.string(),
  companyNumber: z.string(),
  position: z.string(),
  zipcode: z.number(),
  vectors: z.object({
    name: z.array(z.number()),
    address: z.array(z.number()),
  }),
  scores: z.object({
    name: z.number(),
    address: z.number(),
  }),
})
export type AMOwnershipPOCEntityOCOfficer = z.infer<typeof amOwnershipPOCEntityOCOfficerShema>

const amOwnershipPOCEntityInternalDataWithVectorsShema = z.union([
  amOwnershipPOCEntityCherreOwnerShema,
  amOwnershipPOCEntityOCCompanyShema,
  amOwnershipPOCEntityOCOfficerShema,
])

export type AMOwnershipPOCEntityInternalDataWithVectors = z.infer<
  typeof amOwnershipPOCEntityInternalDataWithVectorsShema
>

const amOwnershipPOCEntityInternalDataShema = z.union([
  amOwnershipPOCEntityShema,
  amOwnershipPOCEntityCherreParcelShema,
  amOwnershipPOCEntityCherreOwnerShema,
  amOwnershipPOCEntityOCCompanyShema,
  amOwnershipPOCEntityOCOfficerShema,
])
export type AMOwnershipPOCEntityInternalData = z.infer<typeof amOwnershipPOCEntityInternalDataShema>

export type AMOwnershipPOCEntitiesInternal = Record<string, AMOwnershipPOCEntityInternalData>

export const amOwnershipPOCInternalShema = z.object({
  entities: z.record(amOwnershipPOCEntityInternalDataShema),
  relations: z.record(amOwnershipPOCRelationShema),
})
export type AMOwnershipPOCInternal = z.infer<typeof amOwnershipPOCInternalShema>

export const amOwnershipPOCSimilarityCacheDataShema = z.union([
  amOwnershipPOCEntityCherreOwnerVecotorsDBShema,
  amOwnershipPOCEntityOCCompanyDBShema,
  amOwnershipPOCEntityOCOfficerDBShema,
])
export type AMOwnershipPOCSimilarityCacheData = z.infer<typeof amOwnershipPOCSimilarityCacheDataShema>

export const amOwnershipPOCSimilarityCacheShema = z.record(
  z.array(
    z.object({
      vector: z.array(z.number()),
      data: z.array(amOwnershipPOCSimilarityCacheDataShema),
    })
  )
)
export type AMOwnershipPOCSimilarityCache = z.infer<typeof amOwnershipPOCSimilarityCacheShema>

export const amOwnershipPOCExternalEntityShema = z.object({
  uid: z.string(),
  depth: z.number(),
  type: amOwnershipPOCEntityTypeShema,
  name: z.string(),
  address: z.string(),
  zipcode: z.string().optional(),

  // Parcel
  taxAssessorId: z.number().optional(),
  apn: z.string().optional(),

  // Person
  positions: z.array(
    z.object({
      uid: z.string(),
      organization: z.string(),
      position: z.string(),
    })
  ),

  // Company
  companyType: z.string().optional(),
  currentStatus: z.string().optional(),
  incorporationDate: z.string().optional(),
  dissolutionDate: z.string().optional(),

  // Contact info
  contact: z
    .object({
      emails: z.boolean(),
      workEmail: z.boolean(),
      phoneNumbers: z.boolean(),
      mobilePhone: z.boolean(),
    })
    .optional(),
})

export type AMOwnershipPOCExternalEntity = z.infer<typeof amOwnershipPOCExternalEntityShema>

const amOwnershipPOCExternalEntitiesShema = z.record(amOwnershipPOCExternalEntityShema)

export type AMOwnershipPOCExternalEntities = z.infer<typeof amOwnershipPOCExternalEntitiesShema>

export const amOwnershipPOCExternalShema = z.object({
  entities: amOwnershipPOCExternalEntitiesShema,
  relations: z.record(amOwnershipPOCRelationShema),
})
export type AMOwnershipPOCExternal = z.infer<typeof amOwnershipPOCExternalShema>

export const amOwnershipPOCExportOptionsShema = z.object({
  hops: z.number().min(0).max(4),
  minSimilarScore: z.number().min(0.7).max(1),
})
export type AMOwnershipPOCExportOptions = z.infer<typeof amOwnershipPOCExportOptionsShema>

export const amOwnershipPOCExportFromParcelRequestShema = z.object({
  taxAssessorId: z.number(),
  options: amOwnershipPOCExportOptionsShema,
})
export type AMOwnershipPOCExportFromParcelRequest = z.infer<typeof amOwnershipPOCExportFromParcelRequestShema>

export const amOwnershipPOCPDLAvailableContactsDBShema = z.object({
  uid: z.string(),
  emails: z.boolean(),
  workEmail: z.boolean(),
  phoneNumbers: z.boolean(),
  mobilePhone: z.boolean(),
})
export type AMOwnershipPOCPDLAvailableContactsDB = z.infer<typeof amOwnershipPOCPDLAvailableContactsDBShema>

export const amOwnershipPOCGetFromParcelTaxAssesorIdRequestShema = z.object({
  taxAssessorId: z.string(),
})
export type AMOwnershipPOCGetFromParcelTaxAssesorIdRequest = z.infer<
  typeof amOwnershipPOCGetFromParcelTaxAssesorIdRequestShema
>
